import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileItem, FilesService, UploadMetadata } from '../../shared/services/files.service';
import { HttpEventType } from '@angular/common/http';
import { environment } from '../../../environments/environment';

type UploadedRow = {
  id: string;
  name: string;
  size: number;      // bytes (converted from backend string)
  type: string;
  description: string;
  addedAt: string;   // ISO string
};

@Component({
  selector: 'app-files',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.css']
})
export class FilesComponent {

  // 80 GB example quota (purely for the UI progress bar)
  //TODO GET TOTAL UPLOAD LIMIT from Backend
  quotaBytes = 80 * 1024 * 1024 * 1024;


  // SIGNAL
  fileList = signal<UploadedRow[]>([]);
  isDragging = signal(false);

  //COMPUTED
  totalBytes = computed(() => this.fileList().reduce((s, r) => s + r.size, 0));
  totalFiles = computed(() => this.fileList().length);

  //INJECT
  private fileService = inject(FilesService);
  #destroyRef = inject(DestroyRef);

  private backendBase = 'http://localhost:8080';
  private filesApi = `${this.backendBase}/files`;

  constructor() {
    // no object URLs to clean up anymore, but keeping the hook is harmless
    this.#destroyRef.onDestroy(() => {});
    // initial fetch from backend
    this.loadFiles();
  }

  /**
   * Maps a FileItem object to an UploadedRow object.
   *
   * @param {FileItem} it - The FileItem object to be mapped.
   * @return {UploadedRow} The resulting UploadedRow object.
   */
  private mapToRow(fi: FileItem): UploadedRow {
    return {
      id: String(fi.fileId),
      name: fi.name,
      size: Number(fi.size ?? 0),
      type: fi.type ?? '',
      description: fi.description ?? '',
      addedAt: new Date(fi.uploaded).toISOString(),
    };
  }

  /**
   * Loads files from the file service and maps the retrieved data into row format.
   *
   * The method fetches all files using the file service, transforms the data into a suitable
   * row format, and updates the file list. If an error occurs during the fetch process,
   * the error is logged to the console.
   *
   * @return {void} Does not return any value.
   */
  loadFiles(): void {
    this.fileService.getAllFiles().subscribe({
      next: items => this.fileList.set(items.map(it => this.mapToRow(it))),
      error: err => console.error('Failed to load files:', err),
    });
  }

  /**
   * Handles changes to a file input element when browsing for files.
   * Validates if files are selected, processes them, and resets the file input value.
   *
   * @param {Event} ev The event triggered by the file input element on change.
   * @return {void} This method does not return any value.
   */
  onBrowseChange(ev: Event) {
    const input = ev.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.addFiles(input.files);
    input.value = ''; // allow selecting same file again
  }

  /**
   * Handles the drag over event by preventing the default behavior
   * and updating the internal dragging state.
   *
   * @param {DragEvent} ev - The drag event triggered when an item is being dragged over a target.
   * @return {void} Does not return a value.
   */
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging.set(true);
  }

  /**
   * Handles the event triggered when a drag leaves a valid drop target area.
   * This method updates the dragging state to false.
   *
   * @return {void} Does not*/
  onDragLeave() {
    this.isDragging.set(false);
  }

  /**
   * Handles the drop event when files are dragged and dropped onto a target element.
   * Prevents the default browser behavior, toggles the dragging state, and processes the dropped files if any.
   *
   * @param {DragEvent} ev - The drag event triggered when files are dropped.
   * @return {void} This method does not return a value.
   */
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging.set(false);
    if (ev.dataTransfer?.files?.length) {
      this.addFiles(ev.dataTransfer.files);
    }
  }

  /**
   * Uploads a list of files to the server and handles progress updates for each file.
   *
   * @param {FileList} fileList - A list of files selected by the user for upload.
   * @return {void} This method does not return a value but performs the upload operation
   *                 and triggers UI updates or logging upon progress and completion.
   */
  addFiles(fileList: FileList) {
    for (const f of Array.from(fileList)) {
      const meta: UploadMetadata = { description: '' }; // “Meta” is your description field

      this.fileService.uploadFile(f, meta).subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((100 * event.loaded) / event.total);
            console.log(`Uploading ${f.name}: ${percent}%`);
          }
          if (event.type === HttpEventType.Response) {
            console.log('Upload complete:', event.body);
            this.loadFiles(); // refresh list from backend
          }
        },
        error: err => console.error(`Error uploading ${f.name}:`, err),
      });
    }
  }

  /**
   * If your backend exposes a download endpoint like GET /files/{id}/content,
   * this will trigger the browser download/navigation to that URL.
   */
  download(row: UploadedRow) {
    const url = `${this.filesApi}/${row.id}/content`;
    // Open in the same tab so browser download handlers can kick in
    window.location.href = url;
  }

  /**
   * Removes the specified uploaded row and deletes the associated file.
   *
   * @param {UploadedRow} row - An instance of UploadedRow representing the row to be removed.
   * @return {void} This method does not return any value.
   */
  remove(row: UploadedRow) {
    const id = Number(row.id);
    this.fileService.deleteFile(id).subscribe({
      next: () => this.loadFiles(),
      error: err => console.error('Failed to delete file:', err),
    });
  }

  clearAll() {
    // No mock “clear list” in a backend-driven UI. Keep button disabled in the template.
    console.warn('Clear all is not implemented because files are managed by the backend.');
  }

  /**
   * Converts a size in bytes into a human-readable string format using appropriate units.
   *
   * @*/
  prettySize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /**
   * Calculates and returns the percentage of used storage based on the total bytes and quota bytes.
   *
   * @return {number} The percentage of used storage*/
  usedPct(): number {
    return Math.min(100, Math.round((this.totalBytes() / this.quotaBytes) * 100));
  }

  /**
   * Updates the description of an item in the file list based on its ID.
   *
   * @param {string} id - The unique identifier of the file whose description needs to be updated.
   * @param {string}*/
  updateDescription(id: string, desc: string) {
    this.fileList.update(list => list.map(r => (r.id === id ? { ...r, description: desc } : r)));
  }

  /**
   * Updates the description of a file based on the given row data.
   *
   * @param {UploadedRow} row - The row containing the file ID and the new description.
   * @return {void} This method does not return a value.
   */
  saveDescription(row: UploadedRow) {
    const id = Number(row.id);
    this.fileService.updateFileDescription(id, row.description).subscribe({
      next: () => this.loadFiles(),
      error: err => console.error('Failed to update description:', err),
    });
  }
}
